import { DOCUMENT } from '@angular/common';
import { Renderer2, inject } from '@angular/core';

interface config {
    element: 'script' | 'link';
    typeProperty: 'type' | 'rel';
    type: 'text/javascript' | 'stylesheet' | 'text/partytown';
    src?: 'src' | 'href';
}

type dataProp = Record<string, string>;

export enum TagInjectType {
    SCRIPT = 'script',
    STYLE = 'style',
    CODE = 'code',
}

export const TagInjectConfig: Record<TagInjectType, config> = {
    [TagInjectType.SCRIPT]: {
        element: 'script',
        typeProperty: 'type',
        type: 'text/javascript',
        src: 'src',
    },
    [TagInjectType.STYLE]: {
        element: 'link',
        typeProperty: 'rel',
        type: 'stylesheet',
        src: 'href',
    },
    [TagInjectType.CODE]: {
        element: 'script',
        typeProperty: 'type',
        type: 'text/javascript',
    },
};

export type InjectedScript = Element | HTMLScriptElement | HTMLLinkElement | null;

export interface InjectionOptions {
    ren?: Renderer2;
    doc?: Document;
    dataProsp?: dataProp;
    reuse?: boolean;
}

export async function injectScript(url: string, options?: InjectionOptions): Promise<InjectedScript> {
    return injectTag(url, TagInjectType.SCRIPT, options?.reuse, options?.ren, options?.doc, options?.dataProsp);
}

export async function injectCode(url: string, options?: InjectionOptions): Promise<InjectedScript> {
    return injectTag(url, TagInjectType.CODE, options?.reuse, options?.ren, options?.doc, options?.dataProsp);
}

export async function injectStyle(url: string, options?: InjectionOptions): Promise<InjectedScript> {
    return injectTag(url, TagInjectType.STYLE, options?.reuse, options?.ren, options?.doc, options?.dataProsp);
}

async function injectTag(
    data: string,
    type: TagInjectType.SCRIPT | TagInjectType.STYLE | TagInjectType.CODE,
    reuse = false,
    ren?: Renderer2,
    doc?: Document,
    dp?: dataProp,
): Promise<InjectedScript> {
    const config = TagInjectConfig[type];
    const renderer = ren ?? inject(Renderer2);
    const document = doc ?? inject(DOCUMENT);
    const dataProps = dp ?? {};
    return new Promise((resolve, reject) => {
        const id = `${config.element}-${hashCode(data)}`;
        const exists = document.querySelector(`#${id}`);
        if (reuse && exists) {
            resolve(exists);
            return;
        }
        exists?.remove();
        const script = renderer.createElement(config.element);
        script[config.typeProperty] = config.type;
        for (const [key, value] of Object.entries(dataProps)) {
            renderer.setAttribute(script, `data-${key}`, value);
        }
        setupScript(type, script, config, data, document);
        script.id = id;
        script.addEventListener('error', () => {
            reject(`Failed to load ${data}`);
        });
        script.addEventListener('load', () => {
            resolve(script);
        });
        renderer.appendChild(document.body, script);
    });
}

function setupScript(
    type: TagInjectType,
    script: HTMLScriptElement | HTMLLinkElement,
    config: config,
    data: string,
    document: Document,
) {
    if (type === TagInjectType.CODE) {
        try {
            (script as HTMLScriptElement).appendChild(document.createTextNode(data));
        } catch {
            (script as HTMLScriptElement).text = data;
        }
    } else {
        if (config.src === 'src' && type === TagInjectType.SCRIPT) {
            (script as HTMLScriptElement).src = data;
        } else {
            (script as HTMLLinkElement).href = data;
        }
    }
}

function hashCode(s: string): string {
    let h;
    let i;
    for (i = 0, h = 0; i < s.length; i++) {
        const codePoint = s.codePointAt(i);
        if (codePoint !== undefined) {
            h = Math.trunc(Math.imul(31, h) + codePoint);
        }
    }
    return h.toString();
}
